import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { 
  FaSync, FaPlus, FaPlay, FaPencilAlt, FaTrash, 
  FaAngleDown, FaAngleUp, FaCheck, FaTimes, FaClock,
  FaServer, FaDatabase, FaLaptop, FaBuilding, FaCalendarAlt,
  FaHistory, FaExclamationTriangle, FaInfoCircle, FaSearch, FaStop, FaPause
} from 'react-icons/fa';
import { BsLightningChargeFill } from 'react-icons/bs';

const TallyManagement = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const context = useContext(AuthContext);
  const companyHead = context.user.user;
  const createdBy = companyHead._id;
  
  const [syncs, setSyncs] = useState([]);
  const [editSync, setEditSync] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [scheduleType, setScheduleType] = useState(editSync?.type || "Duration");
  const [syncTypeData, setSyncTypeData] = useState(editSync?.syncTypeData || 'Incremental');
  const [expandedSync, setExpandedSync] = useState({});
  const [company, setCompany] = useState(null);
  const [tallyStatus, setTallyStatus] = useState(false);
  const [currentTallyCompany, setCurrentTallyCompany] = useState("");
  const [tallyCompanies, setTallyCompanies] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState({});
  const [stoppingSync, setStoppingSync] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  
  useEffect(() => {
    const sync = () => setIsDarkMode(localStorage.getItem('darkMode') === 'true');
    window.addEventListener('storage', sync);
    const interval = setInterval(sync, 500);
    return () => { window.removeEventListener('storage', sync); clearInterval(interval); };
  }, []);

  // Polling references
  const pollingInterval = useRef(null);
  const activeSyncs = useRef({});
  const POLL_FREQUENCY = 5000; // Poll every 5 seconds
  
  // Add these state variables with your other state declarations
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedSyncLogs, setSelectedSyncLogs] = useState([]);
  const [selectedSyncName, setSelectedSyncName] = useState("");
  const [logFilters, setLogFilters] = useState({
    type: "all",
    search: "",
    dateRange: {
      start: null,
      end: null
    }
  });

  // Add these state variables for pagination
  const [logPagination, setLogPagination] = useState({
    currentPage: 1,
    logsPerPage: 10,
    totalPages: 1
  });
  const [confirmDeleteLogs, setConfirmDeleteLogs] = useState(false);

  // Add loading state for logs
  const [logsLoading, setLogsLoading] = useState(false);
  const [logCounts, setLogCounts] = useState({
    total: 0,
    success: 0,
    info: 0,
    warning: 0,
    error: 0
  });

  // Add this with your other state variables at the top
  const [currentSyncType, setCurrentSyncType] = useState(editSync?.TallySyncType || '');

  // Helper function to format current time as HH:mm
  const getCurrentTime = () => {
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Add this near your other utility functions
  const clearSyncLoading = (syncId) => {
    setSyncLoading(prev => ({ ...prev, [syncId]: false }));
  };

  // Setup polling mechanism
  useEffect(() => {
    // Start polling for sync updates
    startPolling();
    
    // Clean up polling on unmount
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Start the polling mechanism
  const startPolling = () => {
    // Clear any existing interval first
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // Set up new polling interval
    pollingInterval.current = setInterval(pollSyncUpdates, POLL_FREQUENCY);
  };

  // Function to poll for sync updates
  const pollSyncUpdates = async () => {
    try {
      // Always fetch all syncs to get the latest data
      const response = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      const fetchedSyncs = response.data;
      
      // Compare with current syncs to detect changes and update loading states
      if (fetchedSyncs && fetchedSyncs.length > 0) {
        // The server now reports a real sync status ('running' | 'completed' | 'error' | 'idle')
        const updatedLoadingStates = {};
        
        for (const fetchedSync of fetchedSyncs) {
          // If we previously marked this sync as loading (running)
          if (syncLoading[fetchedSync._id] === true) {
            const isStillRunning = fetchedSync.status === 'running';
            
            if (!isStillRunning) {
              updatedLoadingStates[fetchedSync._id] = false;
              
              // Show a completion notification
              if (activeSyncs.current[fetchedSync._id]) {
                if (fetchedSync.status === 'error') {
                  Swal.fire('Sync Error', 'The sync process finished with errors. Check the logs for details.', 'error');
                } else {
                  Swal.fire('Sync Complete', 'The sync process has been completed.', 'success');
                }
                // Remove from active syncs
                delete activeSyncs.current[fetchedSync._id];
              }
            }
          }
        }
        
        // Update loading states if any changes were detected
        if (Object.keys(updatedLoadingStates).length > 0) {
          setSyncLoading(prev => ({...prev, ...updatedLoadingStates}));
        }
      }
      
      // Update syncs regardless to keep data fresh
      setSyncs(fetchedSyncs);
      
    } catch (error) {
      console.error("Polling error:", error);
    }
  };

  // Initial fetch of company and sync data
  const fetchData = async () => {
    try {
      const companyRes = await axios.get(`${API_BASE}/companies/${companyHead.company._id || companyHead.company}`);
      setCompany(companyRes.data);
      const tallyUrl = companyRes.data.tallyUrl;
      const activeRes = await axios.post(`${API_BASE}/tally/is-tally-active`, { tallyUrl });
      setTallyStatus(activeRes.data.active);
      const currentRes = await axios.post(`${API_BASE}/tally/get-current-active-company`, { tallyUrl });
      setCurrentTallyCompany(currentRes.data.response);
      const companiesRes = await axios.post(`${API_BASE}/tally/get-tally-companies`, { tallyUrl });
      setTallyCompanies(companiesRes.data.response.map(c => c.NAME));
      const licenseRes = await axios.post(`${API_BASE}/tally/get-lincence`, { tallyUrl });
      setLicenseInfo(licenseRes.data.response);
      const syncsRes = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      setSyncs(syncsRes.data);
      
      // Initialize sync loading status based on fetched data
      const initialSyncLoading = {};
      syncsRes.data.forEach(sync => {
        initialSyncLoading[sync._id] = sync.status === 'running';
      });
      setSyncLoading(initialSyncLoading);
      
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch Tally data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [API_BASE, companyHead.company, createdBy]);

  // Refresh button handler to re-fetch syncs and logs
  const handleRefresh = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tally-syncs/${createdBy}`);
      setSyncs(res.data);
      
      // Update sync loading status
      const updatedSyncLoading = {};
      res.data.forEach(sync => {
        updatedSyncLoading[sync._id] = sync.status === 'running';
      });
      setSyncLoading(prev => ({...prev, ...updatedSyncLoading}));
      
      toast.success('Syncs refreshed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh syncs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    if (data.voucherUdfs !== undefined) {
      data.voucherUdfs = String(data.voucherUdfs).split(',').map(s => s.trim()).filter(Boolean);
    }
    try {
      if (editSync) {
        await axios.put(`${API_BASE}/tally-syncs/${editSync._id}`, data);
        toast.success('Sync updated successfully');
      } else {
        await axios.post(`${API_BASE}/tally-syncs`, { ...data, createdBy });
        toast.success('Sync created successfully');
      }
      // Refresh to get updated syncs
      await handleRefresh();
      setOpenDialog(false);
      setEditSync(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  // Update your handleRunSync function to include error handling and SweetAlert confirmation
  const handleRunSync = async (id) => {
    // Use SweetAlert for confirmation
    const result = await Swal.fire({
      title: 'Run Tally Sync?',
      text: "This will synchronize your data with Tally",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, run sync!'
    });
    
    if (result.isConfirmed) {
      try {
        setSyncLoading(prev => ({ ...prev, [id]: true }));
        await axios.post(`${API_BASE}/tally-syncs/run/${id}`);
        toast.success('Sync started successfully');
        
        // Immediately poll once to get updated status
        setTimeout(pollSyncUpdates, 1000);
        
        // Increase polling frequency temporarily when a sync is running
        clearInterval(pollingInterval.current);
        pollingInterval.current = setInterval(pollSyncUpdates, 2000);
        
        // Register this sync as active so we can properly track its completion
        activeSyncs.current[id] = true;
        
        // Setup a safety timeout to clear loading state if other mechanisms fail
        setTimeout(() => {
          clearSyncLoading(id);
          
          // Reset to normal polling frequency
          clearInterval(pollingInterval.current);
          pollingInterval.current = setInterval(pollSyncUpdates, POLL_FREQUENCY);
        }, 60000);
        
      } catch (err) {
        clearSyncLoading(id);
        if (err.response?.status === 409) {
          toast.warning(err.response.data.message || 'Sync is already running');
        } else {
          toast.error(err.response?.data?.message || 'Failed to start sync');
        }
      }
    }
  };

  const handleStopSync = async (id) => {
    const result = await Swal.fire({
      title: 'Stop Sync?',
      text: 'The sync will halt after the current batch of records. Any progress made so far is kept.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, stop it!'
    });

    if (result.isConfirmed) {
      try {
        setStoppingSync(id);
        const res = await axios.post(`${API_BASE}/tally-syncs/stop/${id}`);
        toast.success(res.data.message || 'Stop requested - sync will halt shortly');
        setTimeout(pollSyncUpdates, 1500);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to stop sync');
      } finally {
        setStoppingSync(null);
      }
    }
  };

  const handleToggleSyncEnabled = async (id, enabled) => {
    try {
      await axios.put(`${API_BASE}/tally-syncs/${id}`, { enabled });
      toast.success(enabled ? 'Sync enabled - it will run on its schedule again' : 'Sync paused - scheduled runs stopped');
      setTimeout(pollSyncUpdates, 500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update sync');
    }
  };

  const handleDeleteSync = async (id) => {
    // Use SweetAlert for confirmation
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });
    
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_BASE}/tally-syncs/${id}`);
        setSyncs(syncs.filter(s => s._id !== id));
        Swal.fire(
          'Deleted!',
          'Your sync configuration has been deleted.',
          'success'
        );
      } catch (err) {
        Swal.fire(
          'Error!',
          err.response?.data?.message || 'Failed to delete sync',
          'error'
        );
      }
    }
  };

  const toggleAccordion = (id) => {
    setExpandedSync(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const viewLogsInModal = async (sync) => {
    setSelectedSyncName(sync.name);
    setShowLogModal(true);
    
    // Reset filters and pagination
    setLogFilters({
      type: "all",
      search: "",
      dateRange: {
        start: null,
        end: null
      }
    });
    
    setLogPagination({
      currentPage: 1,
      logsPerPage: 10,
      totalPages: 1
    });
    
    // Fetch initial logs
    await fetchFilteredLogs(sync._id);
  };

  const fetchFilteredLogs = async (syncId) => {
    if (!syncId) {
      // Find the sync ID based on the selected sync name
      const sync = syncs.find(s => s.name === selectedSyncName);
      if (sync) {
        syncId = sync._id;
      } else {
        return;
      }
    }
    
    try {
      setLogsLoading(true);
      
      // Prepare query parameters
      const params = new URLSearchParams({
        page: logPagination.currentPage,
        limit: logPagination.logsPerPage,
        type: logFilters.type,
        search: logFilters.search
      });
      
      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }
      
      const response = await axios.get(`${API_BASE}/tally-syncs/${syncId}/logs?${params.toString()}`);
      
      // Update state with response data
      setSelectedSyncLogs(response.data.logs);
      setLogCounts(response.data.logCounts);
      setLogPagination(prev => ({
        ...prev,
        currentPage: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages
      }));
      
    } catch (error) {
      toast.error('Failed to fetch logs');
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setLogPagination(prev => ({
      ...prev,
      currentPage: pageNumber
    }));
  };

  useEffect(() => {
    if (showLogModal) {
      fetchFilteredLogs();
    }
  }, [logPagination.currentPage, logPagination.logsPerPage, logFilters, showLogModal]);

  const handleFilterChange = (newFilters) => {
    setLogFilters(newFilters);
    // Reset to first page when filters change
    setLogPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    // Fetch will be triggered by the useEffect
  };

  const handleExportLogs = async () => {
    try {
      const syncId = syncs.find(s => s.name === selectedSyncName)?._id;
      if (!syncId) return;
      
      setLogsLoading(true);
      
      // Get all logs with current filters but no pagination
      const params = new URLSearchParams({
        limit: 10000, // Large limit to get all logs
        type: logFilters.type,
        search: logFilters.search
      });
      
      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }
      
      const response = await axios.get(`${API_BASE}/tally-syncs/${syncId}/logs?${params.toString()}`);
      
      // Create CSV content from all logs
      const csvContent = 
        "data:text/csv;charset=utf-8," + 
        "Timestamp,Message,Severity\n" +
        response.data.logs.map(log => {
          // Get severity for each log
          const message = log.message.toLowerCase();
          let severity = "info";
          
          if ((message.includes('error') || message.includes('failed') || message.includes('timeout')) && 
              !message.includes('errored: 0')) {
            severity = "error";
          } else if (message.includes('warning') || message.includes('attention') || 
                    message.includes('could not') || message.includes('retry')) {
            severity = "warning";
          } else if (message.includes('completed') || message.includes('updated:') || 
                    message.includes('created:') || message.includes('sync started') ||
                    message.includes('tally is active') || message.includes('verified') ||
                    message.includes('parsed') || (message.includes('errored: 0') && 
                    (message.includes('updated:') || message.includes('created:')))) {
            severity = "success";
          }
          
          return `"${new Date(log.timestamp).toLocaleString()}","${log.message.replace(/"/g, '""')}","${severity}"`;
        }).join("\n");
      
      // Create download link
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `${selectedSyncName.replace(/\s+/g, '-')}_logs.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      toast.error('Failed to export logs');
      console.error('Error exporting logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDeleteAllLogs = async (syncId) => {
    try {
      const syncToDelete = syncs.find(s => s._id === syncId);
      if (!syncToDelete) {
        toast.error('Sync record not found');
        setConfirmDeleteLogs(false);
        return;
      }
      
      const syncName = syncToDelete.name;
      
      // Show loading state
      toast.info(`Deleting logs for ${syncName}...`);
      
      const response = await axios.delete(`${API_BASE}/tally-syncs/${syncId}/logs`);
      
      if (response.data.success) {
        // Update local state to reflect deleted logs
        setSyncs(prev => prev.map(sync => 
          sync._id === syncId 
            ? {...sync, lastLog: null} 
            : sync
        ));
        
        // Close the log modal and confirmation dialog
        setShowLogModal(false);
        setConfirmDeleteLogs(false);
        
        // Reset log counts
        setLogCounts({
          total: 0,
          success: 0,
          info: 0,
          warning: 0,
          error: 0
        });
        
        toast.success(`Logs for "${syncName}" deleted successfully`);
      } else {
        toast.error(response.data.message || 'Failed to delete logs');
      }
    } catch (err) {
      console.error('Error deleting logs:', err);
      toast.error(err.response?.data?.message || 'Failed to delete logs');
      setConfirmDeleteLogs(false);
    }
  };

  const getLogSeverity = (message) => {
    message = message.toLowerCase();
    
    // Error cases (but not "errored: 0")
    if ((message.includes('error') || message.includes('failed') || message.includes('timeout') || 
        message.includes('errored') || message.includes('errors') || message.includes('skipped')) && 
        !message.includes('errored: 0') && !message.includes('skipped: 0') && !message.includes('Errors 0') &&
        !message.includes('skipped 0')) {
      return 'error';
    }
    
    // Warning cases
    if (message.includes('warning') || message.includes('attention') || 
        message.includes('could not') || message.includes('retry')) {
      return 'warning';
    }
    
    // Success cases
    if (message.includes('completed') || 
        message.includes('Completed') ||
        message.includes('updated:') || 
        message.includes('Updated:') ||
        message.includes('created:') ||
        message.includes('Created:') ||
        message.includes('sync completed') ||
        message.includes('sync started') || 
        message.includes('tally is active') || 
        message.includes('verified') || 
        message.includes('parsed') || 
        (message.includes('errored: 0') && (message.includes('updated:') || message.includes('created:')))) {
      return 'success';
    }
    
    // Info cases (default for any other messages)
    return 'info';
  };

  const getSyncStatus = (sync) => {
    if (sync.enabled === false) return 'paused';
    if (syncLoading[sync._id] || sync.status === 'running') return 'running';
    if (sync.status === 'stopped') return 'stopped';
    if (sync.status === 'error') return 'error';
    if (sync.status === 'completed' || sync.lastRunAt) return 'completed';
    return 'idle';
  };

  const syncStatusUI = (st) => {
    const base = {
      running: { label: 'Running', dot: 'bg-blue-500' },
      error:   { label: 'Error',   dot: 'bg-red-500' },
      stopped: { label: 'Stopped', dot: 'bg-orange-500' },
      completed: { label: 'Completed', dot: 'bg-green-500' },
      idle:    { label: 'Idle',    dot: 'bg-gray-400' },
      paused:  { label: 'Paused',  dot: 'bg-gray-400' }
    }[st] || { label: 'Idle', dot: 'bg-gray-400' };
    const pill = isDarkMode
      ? { running: 'bg-blue-900/50 text-blue-300', error: 'bg-red-900/50 text-red-300', stopped: 'bg-orange-900/50 text-orange-300', completed: 'bg-green-900/50 text-green-300', idle: 'bg-gray-700 text-gray-400', paused: 'bg-gray-700 text-gray-300' }[st]
      : { running: 'bg-blue-100 text-blue-800', error: 'bg-red-100 text-red-800', stopped: 'bg-orange-100 text-orange-800', completed: 'bg-green-100 text-green-800', idle: 'bg-gray-100 text-gray-600', paused: 'bg-gray-200 text-gray-600' }[st];
    return { ...base, classes: pill };
  };

  const formatRunTime = (ts) => {
    if (!ts) return 'Never';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return 'Never';
    const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = bytes, i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(1)} ${units[i]}`;
  };

  const formatDuration = (ms) => {
    if (ms === undefined || ms === null || ms < 0) return '—';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    if (h > 0) return `${h}h ${m % 60}m ${String(s % 60).padStart(2, '0')}s`;
    if (m > 0) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
    return `${s}s`;
  };

  const anySyncRunning = syncs.some(s => s.status === 'running') || Object.values(syncLoading).some(Boolean);

  const pageBg = isDarkMode ? 'bg-gray-900' : 'bg-slate-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const cardText = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const cardMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const cardLabel = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const headingText = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const subText = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const borderCls = isDarkMode ? 'border-gray-600' : 'border-gray-300';
  const borderLighter = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const theadBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50';
  const theadText = isDarkMode ? 'text-gray-300' : 'text-gray-500';
  const tbodyDivider = isDarkMode ? 'divide-gray-700' : 'divide-gray-200';
  const hoverRow = isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50';
  const inputCls = isDarkMode
    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
    : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
  const secondaryBtn = isDarkMode
    ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600'
    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50';
  const accordionBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-50';
  const iconChip = isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
  const logSeverityBg = (sev) => isDarkMode
    ? (sev === 'error' ? 'bg-red-950/40 border-red-500' :
       sev === 'warning' ? 'bg-yellow-950/40 border-yellow-500' :
       sev === 'success' ? 'bg-green-950/40 border-green-500' :
       'bg-blue-950/40 border-blue-500')
    : (sev === 'error' ? 'bg-red-50 border-red-500' :
       sev === 'warning' ? 'bg-yellow-50 border-yellow-500' :
       sev === 'success' ? 'bg-green-50 border-green-500' :
       'bg-blue-50 border-blue-500');
  const summaryChip = (kind) => isDarkMode
    ? (kind === 'ok' ? 'bg-green-900/40 text-green-300' : kind === 'info' ? 'bg-blue-900/40 text-blue-300' : 'bg-red-900/40 text-red-300')
    : (kind === 'ok' ? 'bg-green-50 text-green-700' : kind === 'info' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700');

  useEffect(() => {
    if (editSync) {
      setScheduleType(editSync.type || "Duration");
      setCurrentSyncType(editSync.TallySyncType || '');
      
      // Ensure syncTypeData is always 'Full' for certain sync types
      if (["Godowns", "Stock Groups", "Reorder Status", "Stock Summary", "Groups", "Units", "Ledger", "Voucher Types"].includes(editSync.TallySyncType)) {
        setSyncTypeData('Full');
      } else {
        setSyncTypeData(editSync.syncTypeData || 'Incremental');
      }
    } else {
      setScheduleType("Duration");
      setCurrentSyncType('');
      setSyncTypeData('Incremental');
    }
  }, [editSync]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-screen ${pageBg}`}>
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className={`p-3 sm:p-6 ${pageBg} min-h-screen transition-colors duration-300`}>
      <div className="w-full mx-auto">
        {/* Header with logo */}
        <div className={`mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${cardBg} p-4 sm:p-5 rounded-lg shadow-md`}>
          <div className="flex items-center min-w-0">
            <FaDatabase className={`text-3xl sm:text-4xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} mr-3 flex-shrink-0`} />
            <div className="min-w-0">
              <h1 className={`text-2xl sm:text-3xl font-bold ${headingText} tracking-tight truncate`}>
                Tally Management
              </h1>
              <p className={`${subText} truncate`}>{company?.name || "Company"}</p>
            </div>
          </div>
          <div className="flex items-center flex-shrink-0">
            <span className={`flex items-center px-3 py-1 rounded-full text-sm font-medium 
              ${tallyStatus ? (isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800') : (isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-800')}`}>
              <span className={`w-2 h-2 mr-2 rounded-full ${tallyStatus ? 'bg-green-500' : 'bg-red-500'}`}></span>
              {tallyStatus ? 'Tally Connected' : 'Tally Disconnected'}
            </span>
          </div>
        </div>

        {/* Info Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Tally Status Card */}
          <div className={`${cardBg} rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start`}>
            <div className={`${isDarkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'} p-3 rounded-lg mr-4`}>
              <FaServer className={`${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'} text-xl`} />
            </div>
            <div>
              <h3 className={`${cardMuted} font-medium text-sm uppercase tracking-wider mb-1`}>Tally Status</h3>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${tallyStatus ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`font-semibold ${cardText}`}>{tallyStatus ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
          </div>

          {/* Current Company Card */}
          <div className={`${cardBg} rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start`}>
            <div className={`${isDarkMode ? 'bg-blue-900/40' : 'bg-blue-100'} p-3 rounded-lg mr-4`}>
              <FaBuilding className={`${isDarkMode ? 'text-blue-300' : 'text-blue-600'} text-xl`} />
            </div>
            <div>
              <h3 className={`${cardMuted} font-medium text-sm uppercase tracking-wider mb-1`}>Current Company</h3>
              <p className={`font-semibold ${cardText} truncate max-w-[200px]`}>{currentTallyCompany || 'N/A'}</p>
            </div>
          </div>

          {/* License Info Card */}
          <div className={`${cardBg} rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start`}>
            <div className={`${isDarkMode ? 'bg-yellow-900/40' : 'bg-yellow-100'} p-3 rounded-lg mr-4`}>
              <FaLaptop className={`${isDarkMode ? 'text-yellow-300' : 'text-yellow-600'} text-xl`} />
            </div>
            <div>
              <h3 className={`${cardMuted} font-medium text-sm uppercase tracking-wider mb-1`}>License Info</h3>
              {licenseInfo ? (
                <div className={`space-y-1 text-sm font-medium ${cardText}`}>
                  <p className="truncate">S/N: {licenseInfo.SERIALNUMBER?.substring(0, 8) || 'N/A'}</p>
                  <p className={`text-xs ${subText}`}>User: {licenseInfo.USERLEVEL || 'N/A'}</p>
                </div>
              ) : (
                <p className={cardText}>Not Available</p>
              )}
            </div>
          </div>

          {/* Companies Card */}
          <div className={`${cardBg} rounded-xl shadow-sm hover:shadow-md transition-shadow p-5 flex items-start`}>
            <div className={`${isDarkMode ? 'bg-green-900/40' : 'bg-green-100'} p-3 rounded-lg mr-4`}>
              <FaDatabase className={`${isDarkMode ? 'text-green-300' : 'text-green-600'} text-xl`} />
            </div>
            <div className="w-full">
              <h3 className={`${cardMuted} font-medium text-sm uppercase tracking-wider mb-1`}>Tally Companies</h3>
              <p className={`font-semibold ${cardText}`}>{tallyCompanies.length} companies available</p>
              <details className="mt-1">
                <summary className={`text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} cursor-pointer`}>Show all</summary>
                <ul className={`mt-2 max-h-32 overflow-y-auto text-sm ${subText}`}>
                  {tallyCompanies.map((comp, index) => (
                    <li key={index} className={`truncate py-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} border-b`}>{comp}</li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>

        {/* Sync Configurations Header */}
        <div className={`flex flex-col md:flex-row items-start md:items-center justify-between mb-6 ${cardBg} p-5 rounded-lg shadow-sm`}>
          <h3 className={`flex items-center text-xl font-bold ${headingText}`}>
            <FaCalendarAlt className={`mr-2 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            Sync Configurations
          </h3>
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-3 md:mt-0">
            <button 
              onClick={handleRefresh} 
              className={`${secondaryBtn} px-4 py-2 rounded-lg flex items-center transition-colors duration-200 shadow-sm`}
            >
              <FaSync className="mr-2" /> Refresh
            </button>
            <button 
              onClick={() => setOpenDialog(true)} 
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 
                        flex items-center transition-colors duration-200 shadow-sm"
              disabled={!tallyStatus}
            >
              <FaPlus className="mr-2" /> Create Sync
            </button>
          </div>
        </div>

        {/* Auto-update Notice */}
        <div className={`mb-6 p-3 border-l-4 border-blue-500 rounded flex items-start ${isDarkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-50 text-blue-800'}`}>
          <FaInfoCircle className={`text-blue-500 mr-2 flex-shrink-0 mt-0.5`} />
          <span className="text-sm">
            Logs auto-update every {POLL_FREQUENCY/1000} seconds. Last updated: {new Date().toLocaleTimeString()}
          </span>
        </div>

        {/* Syncs Table */}
        <div className={`overflow-x-auto ${cardBg} rounded-xl shadow-md mb-8`}>
          <table className={`min-w-full divide-y ${tbodyDivider}`}>
            <thead className={theadBg}>
              <tr>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Name
                </th>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Type
                </th>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Status
                </th>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Last Run
                </th>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Schedule
                </th>
                <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${tbodyDivider}`}>
              {syncs.length > 0 ? (
                syncs.map(sync => (
                  <React.Fragment key={sync._id}>
                    <tr className={`${hoverRow} transition-colors duration-150`}>
                      <td className={`px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap font-medium ${cardText}`}>
                        {sync.name} <span className={`text-xs ${subText}`}>({sync.id})</span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                          {sync.TallySyncType}
                        </span>
                        {sync.syncTypeData && (
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                            ${sync.syncTypeData === 'Incremental' 
                              ? (isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800') 
                              : (isDarkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-800')}`}>
                            {sync.syncTypeData}
                          </span>
                        )}
                        {sync.voucherUdfs?.length > 0 && (
                          <span title={`UDFs: ${sync.voucherUdfs.join(', ')}`}
                            className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                            UDF ×{sync.voucherUdfs.length}
                          </span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {(() => {
                          const st = getSyncStatus(sync);
                          const ui = syncStatusUI(st);
                          return (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${ui.classes}`}>
                              <span className={`h-2 w-2 rounded-full mr-2 ${ui.dot} ${st === 'running' ? 'animate-pulse' : ''}`}></span>
                              {st === 'running' && (
                                <div className="animate-spin h-3 w-3 mr-1.5 rounded-full border-2 ${isDarkMode ? 'border-blue-400' : 'border-blue-600'} border-t-transparent"></div>
                              )}
                              {ui.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div>
                          <p className={`text-sm font-medium ${cardText}`}>{formatRunTime(sync.lastRunAt)}</p>
                          {sync.lastRunSummary && (
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                              {typeof sync.lastRunSummary.created === 'number' && (
                                <span className={`px-1.5 py-0.5 rounded ${summaryChip('ok')}`}>C: {sync.lastRunSummary.created}</span>
                              )}
                              {typeof sync.lastRunSummary.updated === 'number' && (
                                <span className={`px-1.5 py-0.5 rounded ${summaryChip('info')}`}>U: {sync.lastRunSummary.updated}</span>
                              )}
                              {typeof sync.lastRunSummary.errored === 'number' && sync.lastRunSummary.errored > 0 && (
                                <span className={`px-1.5 py-0.5 rounded ${summaryChip('bad')}`}>E: {sync.lastRunSummary.errored}</span>
                              )}
                              {typeof sync.lastRunSummary.errored === 'number' && sync.lastRunSummary.errored === 0 && (
                                <span className={`px-1.5 py-0.5 rounded ${summaryChip('ok')}`}>E: 0</span>
                              )}
                            </div>
                          )}
                          {sync.lastRunStats && (
                            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                              <span className={`px-1.5 py-0.5 rounded ${summaryChip('info')}`} title={`Actual wall-clock time this sync run took`}>
                                Duration: {sync.lastRunStats.durationMs ? formatDuration(sync.lastRunStats.durationMs) : '—'}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded ${summaryChip('info')}`} title={`Average CPU cores utilized during the run (CPU time ÷ wall-clock time)`}>
                                Avg cores: {sync.lastRunStats.durationMs > 0 ? `${(sync.lastRunStats.cpuMs / sync.lastRunStats.durationMs).toFixed(2)}` : '—'}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded ${summaryChip('info')}`} title={`Peak memory of the worker process during this run`}>
                                RAM: {formatBytes(sync.lastRunStats.peakRss)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaClock className={`${subText} mr-2`} />
                          <span>
                            {sync.type}: 
                            <span className={`font-medium ml-1 ${cardText}`}>
                              {sync.type === 'Duration' ? `${sync.duration} mins` : sync.time}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                          <button 
                            onClick={() => handleRunSync(sync._id)} 
                            className={`flex items-center transition-colors duration-200
                              ${getSyncStatus(sync) === 'paused'
                                ? 'text-gray-400 cursor-not-allowed'
                                : syncLoading[sync._id] || sync.status === 'running'
                                  ? (isDarkMode ? 'text-blue-400' : 'text-blue-600')
                                  : anySyncRunning
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : (isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800')}`}
                            disabled={syncLoading[sync._id] || sync.status === 'running' || anySyncRunning || getSyncStatus(sync) === 'paused'}
                            title={getSyncStatus(sync) === 'paused'
                              ? 'This sync is paused'
                              : anySyncRunning && !(syncLoading[sync._id] || sync.status === 'running') ? 'Another sync is running' : ''}
                          >
                            {syncLoading[sync._id] || sync.status === 'running' ? (
                              <div className="animate-spin h-4 w-4 mr-1 rounded-full border-2 ${isDarkMode ? 'border-blue-400' : 'border-blue-600'} border-t-transparent"></div>
                            ) : (
                              <FaPlay className="mr-1" />
                            )}
                            {syncLoading[sync._id] || sync.status === 'running' ? 'Running...' : 'Run'}
                          </button>
                          {(syncLoading[sync._id] || sync.status === 'running') && (
                            <button 
                              onClick={() => handleStopSync(sync._id)} 
                              className={`flex items-center transition-colors duration-200
                                ${stoppingSync === sync._id ? 'text-red-300 cursor-wait' : (isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800')}`}
                              disabled={stoppingSync === sync._id}
                              title="Stop this sync after the current batch"
                            >
                              {stoppingSync === sync._id ? (
                                <div className="animate-spin h-4 w-4 mr-1 rounded-full border-2 border-red-600 border-t-transparent"></div>
                              ) : (
                                <FaStop className="mr-1" />
                              )}
                              {stoppingSync === sync._id ? 'Stopping...' : 'Stop'}
                            </button>
                          )}
                          <button 
                            onClick={() => { setEditSync(sync); setOpenDialog(true); setScheduleType(sync.type); }} 
                            className={`${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors duration-200 flex items-center`}
                          >
                            <FaPencilAlt className="mr-1" /> Edit
                          </button>
                          {sync.enabled === false ? (
                            <button 
                              onClick={() => handleToggleSyncEnabled(sync._id, true)} 
                              className={`${isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'} transition-colors duration-200 flex items-center`}
                              title="Re-enable this sync so it runs on its schedule again"
                            >
                              <FaPlay className="mr-1" /> Enable
                            </button>
                          ) : (
                            <button 
                              onClick={() => handleToggleSyncEnabled(sync._id, false)} 
                              className={`transition-colors duration-200 flex items-center
                                ${sync.status === 'running' ? 'text-gray-300 cursor-not-allowed' : (isDarkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-800')}`}
                              disabled={sync.status === 'running'}
                              title={sync.status === 'running' ? 'Stop the running sync instead' : 'Pause this sync (stops scheduled runs)'}
                            >
                              <FaPause className="mr-1" /> Pause
                            </button>
                          )}
                          <button 
                            onClick={() => { setEditSync(sync); setOpenDialog(true); setScheduleType(sync.type); }} 
                            className={`${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} transition-colors duration-200 flex items-center`}
                          >
                            <FaTrash className="mr-1" /> Delete
                          </button>
                          <button 
                            onClick={() => toggleAccordion(sync._id)}
                            className={`${expandedSync[sync._id] ? (isDarkMode ? 'bg-gray-600' : 'bg-gray-200') : ''} 
                              px-3 py-1 rounded ${isDarkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'} transition-colors duration-200 flex items-center`}
                          >
                            {expandedSync[sync._id] ? <FaAngleUp className="mr-1" /> : <FaAngleDown className="mr-1" />}
                            Logs
                          </button>
                          <button 
                            onClick={() => viewLogsInModal(sync)}
                            className={`${isDarkMode ? 'bg-indigo-900/50 text-indigo-300 hover:bg-indigo-800/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} px-3 py-1 rounded transition-colors duration-200 flex items-center`}
                          >
                            <FaHistory className="mr-1" /> View All
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedSync[sync._id] && (
                      <tr>
                        <td colSpan="6" className={`${accordionBg} p-0`}>
                          <div className="max-h-64 overflow-y-auto">
                            {sync.lastLog ? (
                              <div className="p-4">
                                <div className="flex justify-between items-center mb-3">
                                  <h4 className={`font-semibold ${cardLabel} flex items-center`}>
                                    <FaHistory className="mr-2 text-indigo-500" /> Latest Log
                                  </h4>
                                  <button 
                                    onClick={() => pollSyncUpdates()} 
                                    className={`text-xs ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'} flex items-center`}
                                  >
                                    <FaSync className="mr-1 text-xs" /> Refresh
                                  </button>
                                </div>
                                <div className="space-y-2">
                                  {(() => {
                                    const log = sync.lastLog;
                                    const severity = getLogSeverity(log.message.toLowerCase());
                                    return (
                                      <div 
                                        className={`p-3 rounded-lg border-l-4 flex items-start animate-fadeIn ${logSeverityBg(severity)}`}
                                      >
                                        <div className="mr-3 mt-0.5">
                                          {severity === 'error' ? <FaExclamationTriangle className="text-red-500" /> : 
                                           severity === 'warning' ? <FaExclamationTriangle className="text-yellow-500" /> :
                                           severity === 'success' ? <FaCheck className="text-green-500" /> :
                                           <FaInfoCircle className="text-blue-500" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm ${cardText}`} dangerouslySetInnerHTML={{ 
                                            __html: (log.message || '')
                                              .replace(/Updated:\s*(\d+)/gi, 'Updated: <span class="font-semibold text-green-600">$1</span>')
                                              .replace(/Created:\s*(\d+)/gi, 'Created: <span class="font-semibold text-green-600">$1</span>')
                                              .replace(/Errored:\s*0/gi, 'Errored: <span class="font-semibold text-green-600">0</span>')
                                              .replace(/Errored:\s*([1-9]\d*)/gi, 'Errored: <span class="font-semibold text-red-600">$1</span>')
                                              .replace(/Sync started/gi, '<span class="font-semibold text-blue-600">Sync started</span>')
                                              .replace(/Tally is active/gi, '<span class="font-semibold text-green-600">Tally is active</span>')
                                              .replace(/verified/gi, '<span class="font-semibold text-green-600">verified</span>')
                                              .replace(/Parsed/gi, '<span class="font-semibold text-green-600">Parsed</span>')
                                          }} />
                                          <p className={`text-xs ${subText} mt-1`}>
                                            {new Date(log.timestamp).toLocaleString()}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className={`mt-3 text-xs ${subText}`}>
                                  Showing the most recent log entry. Use "View All" for the complete log.
                                </div>
                              </div> 
                            ) : (
                              <div className={`p-8 text-center ${subText} italic`}>
                                No logs available. Run this sync to generate logs.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={`px-4 sm:px-6 py-10 text-center ${subText}`}>
                    <div className="flex flex-col items-center">
                      <FaDatabase className={`${isDarkMode ? 'text-gray-600' : 'text-gray-300'} text-4xl mb-3`} />
                      <p>No sync configurations found.</p>
                      <button 
                        onClick={() => setOpenDialog(true)} 
                        className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
                        disabled={!tallyStatus}
                      >
                        <FaPlus className="mr-2" /> Create Your First Sync
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sync Configuration Modal */}
        {openDialog && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={() => { setOpenDialog(false); setEditSync(null); }}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all">
              <div className={`${cardBg} p-4 sm:p-6 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all scale-in-center`} 
                   onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                  <h3 className={`text-2xl font-bold ${headingText} flex items-center`}>
                    <BsLightningChargeFill className="text-yellow-500 mr-2" />
                    {editSync ? 'Edit' : 'Create'} Sync
                  </h3>
                  <button 
                    onClick={() => { setOpenDialog(false); setEditSync(null); }}
                    className={`${subText} hover:${isDarkMode ? 'text-gray-200' : 'text-gray-700'} focus:outline-none`}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className={`block text-sm font-medium ${cardLabel} mb-1`}>Sync Name</label>
                    <input 
                      name="name" 
                      defaultValue={editSync?.name} 
                      className={`w-full px-3 py-2 ${inputCls} rounded-lg transition-colors`} 
                      placeholder="Enter a descriptive name"
                      required 
                    />
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <label className={`block text-sm font-medium ${cardLabel}`}>Sync Type</label>
                      <div className="ml-2 group relative">
                        <span className="cursor-help text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </span>
                        <div className={`hidden group-hover:block absolute z-10 w-80 ${cardBg} ${isDarkMode ? 'border border-gray-600' : 'border border-gray-300'} rounded-lg p-3 shadow-lg text-sm ${cardLabel}`}>
                          <p className="font-bold mb-1">Voucher Sync:</p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li><span className="font-semibold">Vouchers:</span> Single unified sync that fetches voucher list AND full details (ledger entries, invoice items) using the Tally SDK — no separate list/details steps required.</li>
                            <li><span className="font-semibold">Incremental:</span> Uses Tally's AlterID to fetch only new/changed vouchers.</li>
                            <li><span className="font-semibold">Full:</span> Syncs all vouchers from the start of the financial year.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <select 
                      name="TallySyncType" 
                      value={currentSyncType}
                      className={`w-full px-3 py-2 ${inputCls} rounded-lg transition-colors`}
                      required
                      onChange={(e) => {
                        const selectedType = e.target.value;
                        setCurrentSyncType(selectedType);
                        
                        // Remove 'Voucher Details' from this list
                        if(['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(selectedType)) {
                          setSyncTypeData('Full');
                        }
                      }}
                    >
                      <option value="Stock Items">Stock Items</option>
                      {company?.isStockStatus && (
                        <option value="Stock Summary">Stock Summary</option>
                      )}
                      <option value="Godowns">Godowns</option>
                      {company?.isStockStatus && (
                        <option value="Reorder Status">Reorder Status</option>
                      )}
                      <option value="Stock Groups">Stock Groups</option>
                      <option value="Groups">Groups</option>
                      <option value="Units">Units</option>
                      <option value="Ledger">Ledger</option>
                      <option value="Voucher Types">Voucher Types</option>
                      <option value="Simple Trial Balance">Simple Trial Balance</option>
                      <optgroup label="Voucher Sync Options">
                        <option value="Vouchers">Vouchers</option>
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${cardLabel} mb-1`}>Sync Data Type</label>
                    <div className="grid grid-cols-2 gap-4 mt-1">
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${syncTypeData === 'Incremental' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 
                          (isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50')} 
                        ${['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(currentSyncType) ? 'opacity-50' : ''}`}>
                        <input 
                          type="radio" 
                          name="syncTypeData" 
                          value="Incremental" 
                          checked={syncTypeData === 'Incremental'}
                          onChange={() => setSyncTypeData('Incremental')}
                          className="sr-only" 
                          disabled={['Godowns', 'Stock Groups', 'Reorder Status', 'Stock Summary', 'Groups', 'Units', 'Ledger', 'Voucher Types', 'Simple Trial Balance'].includes(currentSyncType)}
                        />
                        <FaSync className="mr-2" />
                        Incremental
                      </label>
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${syncTypeData === 'Full' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : 
                          (isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50')}`}>
                        <input 
                          type="radio" 
                          name="syncTypeData" 
                          value="Full" 
                          checked={syncTypeData === 'Full'}
                          onChange={() => setSyncTypeData('Full')}
                          className="sr-only" 
                        />
                        <FaDatabase className="mr-2" />
                        Full
                      </label>
                    </div>
                  </div>
                  {currentSyncType === 'Vouchers' && (
                    <div>
                      <label className={`block text-sm font-medium ${cardLabel} mb-1`}>UDF Fields</label>
                      <input 
                        name="voucherUdfs" 
                        defaultValue={editSync?.voucherUdfs?.length ? editSync.voucherUdfs.join(', ') : ''}
                        className={`w-full px-3 py-2 ${inputCls} rounded-lg transition-colors`} 
                        placeholder="VchPaymentMode, VchAgentName, EI Vehicle"
                      />
                      <p className={`mt-1 text-xs ${subText}`}>
                        Optional — comma-separated User Defined Field names, exactly as configured on the voucher types in Tally.
                        Only fetched for Vouchers syncs; Tally returns whatever UDF data exists on each voucher.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className={`block text-sm font-medium ${cardLabel} mb-1`}>Schedule Type</label>
                    <div className="grid grid-cols-2 gap-4 mt-1"> 
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${scheduleType === 'Duration' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : (isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50')}`}>
                        <input 
                          type="radio" 
                          name="type" 
                          value="Duration" 
                          checked={scheduleType === 'Duration'}
                          onChange={(e) => setScheduleType(e.target.value)}
                          className="sr-only" 
                        />
                        <FaSync className="mr-2" />
                        Interval
                      </label>
                      <label className={`flex items-center justify-center border rounded-lg px-3 py-2 cursor-pointer transition-colors 
                        ${scheduleType === 'Time' ? 
                          'border-indigo-500 bg-indigo-50 text-indigo-800' : (isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50')}`}>
                        <input 
                          type="radio" 
                          name="type" 
                          value="Time" 
                          checked={scheduleType === 'Time'}
                          onChange={(e) => setScheduleType(e.target.value)}
                          className="sr-only" 
                        />
                        <FaClock className="mr-2" />
                        Daily Time
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${cardLabel} mb-1`}>
                      {scheduleType === 'Time' ? 'Specific Time' : 'Interval (minutes)'}
                    </label>
                    <input 
                      name={scheduleType === 'Time' ? 'time' : 'duration'} 
                      type={scheduleType === 'Time' ? 'time' : 'number'} 
                      min={scheduleType === 'Time' ? undefined : "1"}
                      defaultValue={
                        scheduleType === 'Time'
                          ? (editSync?.time ? editSync.time : getCurrentTime())
                          : (editSync?.duration || '60')
                      }
                      className={`w-full px-3 py-2 ${inputCls} rounded-lg transition-colors`} 
                      required 
                    />
                    <p className={`mt-1 text-xs ${subText}`}>
                      {scheduleType === 'Time' 
                        ? 'Sync will run once daily at this time' 
                        : 'Sync will run repeatedly at this interval'}
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button 
                      type="button" 
                      onClick={() => { setOpenDialog(false); setEditSync(null); }} 
                      className={`px-4 py-2 ${secondaryBtn} rounded-lg transition-colors`}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <FaCheck className="mr-2" /> 
                      {editSync ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

        {/* Logs Modal */}
        {showLogModal && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity" onClick={() => setShowLogModal(false)}></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 transition-all">
              <div className={`${cardBg} rounded-xl shadow-xl w-full max-w-4xl h-[80vh] flex flex-col transform transition-all scale-in-center`}
                   onClick={e => e.stopPropagation()}>
                <div className={`p-4 sm:p-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} border-b flex flex-wrap justify-between items-center gap-3`}>
                  <h3 className={`text-lg sm:text-xl font-bold ${headingText} flex items-center`}>
                    <FaHistory className="text-indigo-500 mr-2" />
                    <span className="break-words">Logs for {selectedSyncName}</span>
                  </h3>
                  <div className="flex items-center">
                    <button 
                      onClick={() => {
                        const syncId = syncs.find(sync => sync.name === selectedSyncName)?._id;
                        if (syncId) {
                          handleDeleteAllLogs(syncId);
                        } else {
                          toast.error('Sync record not found');
                        }
                      }}
                      className={`${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'} mr-4 flex items-center`}
                      disabled={selectedSyncLogs.length === 0}
                    >
                      <FaTrash className="mr-1" /> Delete All Logs
                    </button>
                    <button 
                      onClick={() => setShowLogModal(false)} 
                      className={`${subText} hover:${isDarkMode ? 'text-gray-200' : 'text-gray-700'} focus:outline-none`}
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Filters Section */}
                <div className={`p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} ${isDarkMode ? 'border-gray-600' : 'border-gray-200'} border-b`}>
                  <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
                    <div className="flex-1">
                      <label className={`text-sm ${subText} mb-1 block`}>Search Messages</label>
                      <input 
                        type="text" 
                        value={logFilters.search}
                        onChange={(e) => handleFilterChange({...logFilters, search: e.target.value})}
                        placeholder="Filter by message content..." 
                        className={`w-full px-3 py-2 ${inputCls} rounded-lg`}
                      />
                    </div>
                    <div>
                      <label className={`text-sm ${subText} mb-1 block`}>Status Type</label>
                      <select 
                        value={logFilters.type}
                        onChange={(e) => handleFilterChange({...logFilters, type: e.target.value})}
                        className={`w-full px-3 py-2 ${inputCls} rounded-lg`}
                      >
                        <option value="all">All Messages</option>
                        <option value="success">Success</option>
                        <option value="info">Informational</option>
                        <option value="warning">Warnings</option>
                        <option value="error">Errors</option>
                      </select>
                    </div>
                    <div>
                      <label className={`text-sm ${subText} mb-1 block`}>Date Range</label>
                      <div className="flex flex-wrap gap-2 items-center">
                        <input 
                          type="date" 
                          value={logFilters.dateRange.start || ''}
                          onChange={(e) => handleFilterChange({
                            ...logFilters, 
                            dateRange: {...logFilters.dateRange, start: e.target.value}
                          })}
                          className={`w-32 px-2 py-2 ${inputCls} rounded-lg`}
                        />
                        <span className={subText}>to</span>
                        <input 
                          type="date" 
                          value={logFilters.dateRange.end || ''}
                          onChange={(e) => handleFilterChange({
                            ...logFilters, 
                            dateRange: {...logFilters.dateRange, end: e.target.value}
                          })}
                          className={`w-32 px-2 py-2 ${inputCls} rounded-lg`}
                        />
                        <button 
                          onClick={() => handleFilterChange({
                            ...logFilters,
                            dateRange: { start: null, end: null }
                          })} 
                          className={`${subText} hover:${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}
                          title="Clear date filter"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Filter Status Bar */}
                  <div className="mt-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className={subText}>
                        Showing <span className="font-medium">{selectedSyncLogs.length}</span> of <span className="font-medium">{logCounts.total}</span> logs
                      </span>
                      {/* Log type indicator counts */}
                      <div className="flex flex-wrap ml-4 gap-x-3 gap-y-1">
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-1"></div>
                          Success: {logCounts.success}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
                          Info: {logCounts.info}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1"></div>
                          Warning: {logCounts.warning}
                        </span>
                        <span className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-red-500 mr-1"></div>
                          Error: {logCounts.error}
                        </span>
                      </div>
                    </div>
                    
                    {/* Display logs per page selector */}
                    <div className="flex items-center">
                      <span className={`${subText} mr-2`}>Logs per page:</span>
                      <select 
                        value={logPagination.logsPerPage}
                        onChange={(e) => setLogPagination({
                          ...logPagination,
                          logsPerPage: parseInt(e.target.value),
                          currentPage: 1
                        })}
                        className={`${borderCls} border rounded px-2 py-1 text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'}`}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                    
                    <button 
                      onClick={() => handleFilterChange({
                        type: "all",
                        search: "",
                        dateRange: {
                          start: null,
                          end: null
                        }
                      })} 
                      className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
                
                {/* Logs Table */}
                <div className="flex-1 overflow-y-auto overflow-x-auto">
                  {logsLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
                    </div>
                  ) : (
                    <table className={`min-w-full divide-y ${tbodyDivider}`}>
                      <thead className={theadBg}>
                        <tr>
                          <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                            Timestamp
                          </th>
                          <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                            Message
                          </th>
                          <th className={`px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium ${theadText} uppercase tracking-wider`}>
                            Severity
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${tbodyDivider}`}>
                        {selectedSyncLogs.map((log, index) => {
                          const severity = getLogSeverity(log.message.toLowerCase());
                          return (
                            <tr key={index} className={`${hoverRow} transition-colors duration-150 ${logSeverityBg(severity)}`}>
                              <td className={`px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm ${subText}`}>
                                {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className={`px-4 sm:px-6 py-3 sm:py-4 text-sm ${cardText}`}>
                                {log.message}
                              </td>
                              <td className={`px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm ${cardText}`}>
                                {severity.charAt(0).toUpperCase() + severity.slice(1)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                
                {/* Pagination */}
                <div className={`p-4 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-t flex items-center justify-between`}>
                  <button 
                    onClick={() => handlePageChange(logPagination.currentPage - 1)} 
                    className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                    disabled={logPagination.currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className={`text-sm ${subText}`}>
                    Page {logPagination.currentPage} of {logPagination.totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(logPagination.currentPage + 1)} 
                    className={`${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                    disabled={logPagination.currentPage === logPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TallyManagement;